import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CoreFormModalComponent } from '@app/core/components/core-modals/core-form-modal/core-form-modal.component';
import { IEidosLightFormField } from '@app/core/components/eidos-light-form/eidos-light-form.component';
import { IDynamicApiErrorResponse, IDynamicApiResponse } from '@app/core/models/dynamic-api-response.model';
import { CoreModalService, ModalRef } from '@app/core/services/core-modal.service';
import { EidosUtilityIconsService } from '@app/core/services/eidos-utility-icons.service';
import {
  IResPaymentMilestoneChange,
  ResPaymentMilestone,
} from '@app/reservation/models/res-booking.model';
import { ReservationApiService } from '@app/reservation/services/reservation-api.service';
import { ReservationService } from '@app/reservation/services/reservation.service';
import { RescomService } from '@app/res-common/services/rescom.service';
import { DxDataGridComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { DateTime } from 'luxon';

type EditableMilestoneField = 'dueDate' | 'milestoneAmount';

interface MilestoneOriginalValues {
  dueDate: string;
  milestoneAmount: number;
}

@Component({
  selector: 'res-payment-milestone',
  templateUrl: './res-payment-milestone.component.html',
  styleUrls: ['./res-payment-milestone.component.scss'],
  standalone: false,
})
export class ResPaymentMilestoneComponent implements OnInit {
  @Input() bkgID: number = 0;
  @Input() modalRef?: ModalRef;

  @ViewChild(DxDataGridComponent) grid?: DxDataGridComponent;

  rows: ResPaymentMilestone[] = [];
  loading = false;
  saving = false;
  hasChanges = false;

  private originals = new Map<number, MilestoneOriginalValues>();
  private dirtyCells = new Set<string>();

  constructor(
    private reservationApiService: ReservationApiService,
    private reservationService: ReservationService,
    private rescomService: RescomService,
    private coreModalService: CoreModalService,
    protected eidosUtilityIconsService: EidosUtilityIconsService,
  ) {
  }

  ngOnInit() {
    this.load();
  }

  private load() {
    this.loading = true;
    this.reservationApiService.getPaymentMilestone(this.bkgID).subscribe({
      next: (rows) => {
        this.loading = false;
        this.originals.clear();
        this.dirtyCells.clear();
        this.hasChanges = false;
        rows.forEach((row) => {
          this.originals.set(row.rowID, {
            dueDate: this.dateKey(row.dueDate),
            milestoneAmount: row.milestoneAmount,
          });
        });
        this.rows = rows;
      },
      error: (err: unknown) => {
        this.loading = false;
        const apiError = err as IDynamicApiResponse;
        this.rescomService.utilityDialogs.error({
          message: 'Error. Payment Milestones Not Loaded.',
          error: apiError?.Errors?.map((e: IDynamicApiErrorResponse) => e.ErrorDesc),
          title: 'ERROR - Payment Milestones',
          size: '380px',
        });
      },
    });
  }

  onEditingStart(event: {
    cancel?: boolean | PromiseLike<boolean>;
    data?: ResPaymentMilestone;
    column?: { dataField?: string };
  }) {
    const field = event.column?.dataField;
    if (field === 'milestoneAmount' && !event.data?.canEditAmount) event.cancel = true;
  }

  onCellPrepared(event: {
    rowType?: string;
    data?: ResPaymentMilestone;
    column?: { dataField?: string };
    cellElement?: HTMLElement;
  }) {
    if (event.rowType !== 'data' || !event.data || !event.cellElement) return;

    const field = event.column?.dataField as EditableMilestoneField | undefined;
    if (field !== 'dueDate' && field !== 'milestoneAmount') return;

    const canEdit = field === 'dueDate' ? event.data.canEditDate : event.data.canEditAmount;
    if (!canEdit) return;

    const dirty = this.isDirty(event.data.rowID, field);
    event.cellElement.classList.toggle('payment-milestone__cell--editable', !dirty);
    event.cellElement.classList.toggle('payment-milestone__cell--dirty', dirty);
  }

  openEditDueDate(row: ResPaymentMilestone) {
    if (!row.canEditDate) return;

    const fields: IEidosLightFormField[] = [
      {
        field: 'dueDate',
        caption: 'Due Date',
        type: 'date',
        width: 'col-12',
        required: true,
        showClearButton: false,
        displayFormat: 'dd MMM yyyy',
      },
    ];

    this.coreModalService
      .open<CoreFormModalComponent, { fields: IEidosLightFormField[]; values: { dueDate: DateTime } }, { dueDate: DateTime }>(
        CoreFormModalComponent,
        {
          title: 'Edit Due Date',
          data: {
            fields,
            values: { dueDate: row.dueDate },
          },
          backdropClass: '',
          dimensions: { width: '400px' },
        },
      )
      .subscribe((data) => {
        if (!data) return;

        const dueDate = data.dueDate?.isValid ? data.dueDate : DateTime.invalid('empty');
        if (this.dateKey(dueDate) === this.dateKey(row.dueDate)) return;

        row.dueDate = dueDate;
        this.markDirty(
          row.rowID,
          'dueDate',
          this.dateKey(dueDate) !== this.originals.get(row.rowID)?.dueDate,
        );
        const index = this.rows.findIndex((r) => r.rowID === row.rowID);
        if (index >= 0) this.grid?.instance.repaintRows([index]);
      });
  }

  onMilestoneAmountChanged(
    cellInfo: { data: ResPaymentMilestone; setValue: (value: number) => void },
    event: { event?: unknown; value?: number | null; component: { option: (name: string, value: number) => void } },
  ) {
    if (!event.event) return;
    let value = event.value ?? 0;
    const max = cellInfo.data.maxMilestoneAmount;
    if (value > max) {
      value = max;
      event.component.option('value', max);
    }
    cellInfo.setValue(value);
    this.markDirty(cellInfo.data.rowID, 'milestoneAmount', value !== this.originals.get(cellInfo.data.rowID)?.milestoneAmount);
  }

  validateMilestoneAmount = (event: { value?: number | null; data?: ResPaymentMilestone }) => {
    const max = event.data?.maxMilestoneAmount;
    if (max == null || event.value == null) return true;
    return event.value <= max;
  };

  private buildChanges(): IResPaymentMilestoneChange[] {
    const dirtyRowIDs = new Set(
      [...this.dirtyCells].map((key) => Number(key.split(':')[0]))
    );

    return this.rows
      .filter((row) => dirtyRowIDs.has(row.rowID))
      .map((row) => ({
        SourceType: row.sourceType,
        SectionID: row.sectionID,
        ServiceID: row.serviceID > 0 ? row.serviceID : undefined,
        OptionID: row.optionID > 0 ? row.optionID : undefined,
        ExperienceID: row.experienceID > 0 ? row.experienceID : undefined,
        IsFinalPayment: row.isFinalPayment ? 'Y' : 'N',
        Seq: row.seq,
        PaxGroupID: row.paxGroupID,
        DueDate: this.dateKey(row.dueDate),
        Amount: row.milestoneAmount,
      }));
  }

  private isDirty(rowID: number, field: EditableMilestoneField) {
    return this.dirtyCells.has(this.dirtyKey(rowID, field));
  }

  private markDirty(rowID: number, field: EditableMilestoneField, dirty: boolean) {
    const key = this.dirtyKey(rowID, field);
    if (dirty) this.dirtyCells.add(key);
    else this.dirtyCells.delete(key);
    this.hasChanges = this.dirtyCells.size > 0;
  }

  private dirtyKey(rowID: number, field: EditableMilestoneField) {
    return `${rowID}:${field}`;
  }

  private dateKey(value?: DateTime | null) {
    return value?.isValid ? value.toISODate() ?? '' : '';
  }

  close() {
    this.modalRef?.close();
  }

  save() {
    if (!this.hasChanges || this.saving || this.loading) return;

    const changes = this.buildChanges();
    if (!changes.length) return;

    this.saving = true;
    this.reservationApiService.managePaymentMilestone({ BkgID: this.bkgID, Changes: changes }).subscribe({
      next: () => {
        this.saving = false;
        notify({ message: 'Payment milestones saved', type: 'success', position: 'bottom', displayTime: 3000 });
        this.reservationService.bookingUtility.reloadCurrentBkg().finally(() => this.load());
      },
      error: (err: unknown) => {
        this.saving = false;
        const apiError = err as IDynamicApiResponse;
        this.rescomService.utilityDialogs.error({
          message: 'Error. Payment Milestones Not Saved.',
          error: apiError?.Errors?.map((e: IDynamicApiErrorResponse) => e.ErrorDesc),
          title: 'ERROR - Payment Milestones',
          size: '380px',
        });
      },
    });
  }

}
